import React, { useState, useContext } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { SignDocTypes, OutcomeStatus } from '../SignDocLib';
import { get } from 'lodash';
import './SignDocApprovalActions.scss';
import { I18nReactContext } from '../../../contexts/I18nContext';

/**
 * SignDocApprovalActions Component
 *
 * Reusable component for rendering approve/reject action buttons for SignDocs.
 * Supports multiple SignDoc types with different tooltips and disable logic.
 *
 * Use Cases:
 * 1. UserDetailsModal - Course attendance and certification approval
 * 2. SignDocUsersTable - Course reports attendance marking
 * 3. CourseAttendanceTable - Pending course attendance approvals
 * 4. Any other approval workflow for SignDocs
 *
 * @param {object} props - Component props
 * @param {object} props.row - The data row containing SignDoc information
 * @param {string} props.signDocType - The SignDoc type (e.g., 'FRONTAL_COURSE', 'ONLINE_COURSE', 'UPLOAD_DOCUMENT')
 * @param {boolean} props.isCurrentUserApprover - Whether the current user is authorized to approve
 * @param {Function} props.onApprove - Callback function when approve button is clicked
 * @param {Function} props.onReject - Callback function when reject button is clicked
 * @param {object} props.i18n - Internationalization object for translations
 * @param {boolean} [props.isCompleted] - Whether the SignDoc is completed (optional, defaults based on row data)
 * @param {string} [props.outcomeStatus] - The outcome status (optional, defaults based on row data)
 * @param {boolean} [props.showActions] - Override to force show/hide actions (optional, defaults based on type)
 * @param {string} [props.approveTooltip] - Custom approve tooltip (optional, defaults based on type)
 * @param {string} [props.rejectTooltip] - Custom reject tooltip (optional, defaults based on type)
 * @param {boolean} [props.disableApprove] - Custom disable logic for approve button (optional)
 * @param {boolean} [props.disableReject] - Custom disable logic for reject button (optional)
 * @returns {JSX.Element|null} Rendered action buttons or null if not authorized
 */
export const SignDocApprovalActions = ({
    row,
    signDocType,
    isCurrentUserApprover,
    onApprove,
    onReject,
    isCompleted,
    outcomeStatus,
}) => {
    const {i18n} = useContext(I18nReactContext);
    const [isApproving, setIsApproving] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);

    // Authorization check - only show actions if user is an approver
    if (!isCurrentUserApprover) {
        return null;
    }

    // Determine if this SignDoc type supports actions
    const isCourse = signDocType === SignDocTypes.ONLINE_COURSE || signDocType === SignDocTypes.FRONTAL_COURSE;
    const isUploadDocument = signDocType === SignDocTypes.UPLOAD_DOCUMENT;
    const isApprovalsEnabled = get(row, 'signDoc.additionalData.enableApprovals', false);

    // Default showActions logic if not explicitly provided
    const shouldShowActions = isCourse || (isUploadDocument && isApprovalsEnabled);

    if (!shouldShowActions) {
        return null;
    }

    // Extract status information from row if not provided
    const completed = isCompleted !== undefined
        ? isCompleted
        : get(row, 'completedOnDate') != null || get(row, 'signDocData.completedOnDate') != null;

    const outcome = outcomeStatus !== undefined
        ? outcomeStatus
        : get(row, 'outcomeStatus') || get(row, 'signDocData.outcomeStatus');

    // Default tooltips based on SignDoc type
    const approveTooltip = isCourse
        ? i18n.translate('userDetailsModal.actions.markAttended')
        : i18n.translate('userDetailsModal.actions.updateExpiryDate');

    const rejectTooltip = isCourse
        ? i18n.translate('userDetailsModal.actions.markNoShow')
        : i18n.translate('userDetailsModal.actions.declineApproval');

    const finalApproveTooltip = approveTooltip;
    const finalRejectTooltip = rejectTooltip;

    // Default disable logic for courses
    const disableApprove = isCourse && completed && outcome !== OutcomeStatus.NO_SHOW;
    const disableReject = isCourse && outcome === OutcomeStatus.NO_SHOW;

    // Handlers with loading state management
    const handleApproveClick = async () => {
        setIsApproving(true);
        try {
            await onApprove(row);
        } finally {
            setIsApproving(false);
        }
    };

    const handleRejectClick = async () => {
        setIsRejecting(true);
        try {
            await onReject(row);
        } finally {
            setIsRejecting(false);
        }
    };

    // Disable buttons during loading
    return (
        <div className="SignDocApprovalActions">
            <Tooltip title={finalApproveTooltip}>
                <span>
                    <IconButton
                        size="small"
                        color="success"
                        onClick={handleApproveClick}
                        disabled={disableApprove}
                    >
                        {isApproving ? (
                            <CircularProgress size={20} color="success" />
                        ) : (
                            <CheckCircleIcon fontSize="small" />
                        )}
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title={finalRejectTooltip}>
                <span>
                    <IconButton
                        size="small"
                        color="error"
                        onClick={handleRejectClick}
                        disabled={disableReject}
                    >
                        {isRejecting ? (
                            <CircularProgress size={20} color="error" />
                        ) : (
                            <CancelIcon fontSize="small" />
                        )}
                    </IconButton>
                </span>
            </Tooltip>
        </div>
    );
};
